<template>
  <div id="lineManagerDetails">
    <div
      v-if="manager"
      class="data"
    >
      <el-page-header
        title="Go Back"
        :content="null"
        @back="$router.go(-1)"
      />

      <el-card
        class="managerCard mainCard clear-card"
        shadow="hover"
      >
        <el-row
          type="flex"
          class="row1"
        >
          <el-col
            :span="12"
            class="left"
          >
            <span class="name">
              {{ manager.name }}
            </span>
            <span class="email">
              {{ manager.email }}
            </span>
          </el-col>
          <el-col
            :span="12"
            class="right"
          >
            <el-button
              v-if="editMode === false"
              round
              class="clearBtn"
              @click="editMode = true"
            >
              Edit <i class="el-icon-edit"></i>
            </el-button>
            <el-button
              v-if="editMode === true"
              round
              @click="editMode = false"
            >
              Save
            </el-button>
          </el-col>
        </el-row>

        <el-row
          type="flex"
          class="row3"
        >
          <el-col
            :span="10"
            class="col1"
          >
            <div class="label">
              Team
            </div>
            <el-select
              v-if="editMode"
              v-model="manager.sub_group_uuid"
              placeholder="Select"
              @change="updateTeam"
            >
              <el-option
                v-for="(team, key) in teams"
                :key="key"
                :label="team.name"
                :value="team.uuid"
              />
            </el-select>
            <div v-else>
              {{ currentTeam }}
            </div>
          </el-col>
          <el-col
            :span="10"
            class="col2"
          >
            <div class="label">
              Leaders
            </div>
            <div class="manager">
              4
            </div>
          </el-col>
          <el-col
            :span="4"
            class="col2"
          >
            <div class="label">
              Availability
            </div>
            <el-radio-group
              v-if="editMode"
              v-model="manager.is_active"
              placeholder="Select"
              @change="updateAvailability"
            >
              <el-radio
                v-for="(option, key) in availabilityOptions"
                :key="key"
                :label="option.value"
              >
                {{ option.label }}
              </el-radio>
            </el-radio-group>
            <div v-else>
              {{ manager.is_active ? 'Active' : 'Deactivated' }}
            </div>
          </el-col>
        </el-row>
      </el-card>

      <el-table
        v-if="manager"
        :data="manager.leaders"
        style="width: 100%;"
        class="leadersList clear-table mainCard"
        empty-text="This manager doesn't have leaders yet."
      >
        <el-table-column
          prop="name"
          label="Leaders"
        />
        <el-table-column
          prop="email"
          label="Email"
        />
        <el-table-column
          label="Session"
        >
          <template
            slot-scope="scope"
          >
            {{ scope.row.session_id | formatSession }}
          </template>
        </el-table-column>
      </el-table>
    </div>

    <div
      v-else
      class="noData"
    >
      <el-card
        shadow="hover"
        class="mainCard clear-card"
      >
        No Line Manager found
      </el-card>
    </div>
  </div>
</template>

<script>
import api from '@/api';

export default {
  filters: {
    formatSession(value) {
      let text = '0';
      if (value.includes('session_')) {
        text = value.replace('session_', '');
      }

      return text;
    },
  },

  data() {
    return {
      editMode: false,
      manager: null,
      teams: [],
      teamBackup: '',
      availabilityBackup: '',
      availabilityOptions: [
        {
          value: true,
          label: 'Active',
        },
        {
          value: false,
          label: 'Inactive',
        },
      ],
    };
  },

  computed: {
    currentTeam() {
      const team = this.teams.find((el) => el.uuid === this.manager.sub_group_uuid);
      return team.name;
    },
  },

  created() {
    this.getManager();
    this.getTeamsList();
  },

  methods: {
    async getManager() {
      try {
        const response = await api.getSponsorDetails(this.$route.params.id);
        this.manager = response.data;
        this.teamBackup = response.data.sub_group_uuid;
        this.availabilityBackup = response.data.is_active;
      } catch (err) {
        if (err.msg) {
          this.$message.error(err.msg);
        }
      }
    },

    async getTeamsList() {
      try {
        const response = await api.getSimpleTeams();
        this.teams = response.data;
      } catch (err) {
        if (err.msg) {
          this.$message.error(err.msg);
        }
      }
    },

    async updateTeam() {
      const manager = this.manager.name ? this.manager.name : this.manager.email;
      const team = this.teams.find((el) => el.uuid === this.manager.sub_group_uuid);

      this.$confirm(`Are you sure you want to change teams to ${team.name} for ${manager}?`, '', {
        confirmButtonText: 'Save change',
        cancelButtonText: 'Back',
        roundButton: true,
        customClass: 'lineManagerConfirmation',
      }).then(async () => {
        try {
          const response = await api.changeLeaderTeam(
            this.manager.uuid, this.manager.sub_group_uuid,
          );
          this.manager.sub_group_uuid = response.data.uuid;
          this.$message({
            message: 'Line Manager\'s team changed.',
            type: 'success',
          });
          this.teamBackup = this.manager.sub_group_uuid;
          // we need to update list of leaders associated with this manager
          this.getManager();
        } catch (err) {
          if (err.msg) {
            this.$message.error(err.msg);
          }
        }
      }).catch(() => {
        this.manager.sub_group_uuid = this.teamBackup;
      });
    },

    async updateAvailability() {
      const manager = this.manager.name ? this.manager.name : this.manager.email;
      const action = this.manager.is_active ? 'activate' : 'deactivate';

      this.$confirm(`Are you sure you want to ${action} ${manager}?`, '', {
        confirmButtonText: 'Save change',
        cancelButtonText: 'Back',
        roundButton: true,
        customClass: 'lineManagerConfirmation',
      }).then(async () => {
        try {
          const response = await api.changeLeaderAvailability(this.manager.uuid);
          this.manager.is_active = response.data.is_active;
          this.$message({
            message: 'Line Manager\'s availability updated.',
            type: 'success',
          });
          this.availabilityBackup = this.manager.is_active;
        } catch (err) {
          if (err.msg) {
            this.$message.error(err.msg);
          }
        }
      }).catch(() => {
        this.manager.is_active = this.availabilityBackup;
      });
    },
  },

};
</script>

<style lang="scss">
#lineManagerDetails {
  .label {
    font-size: 13px;
  }

  .noData {
    margin-bottom: 20px;
  }

  .managerCard {
    margin-bottom: 20px;

    .el-card__body {
      text-align: left;

      .el-row:not(:last-child) {
        margin-bottom: 30px;
      }

      .row1 {
        .left {
          .name {
            font-weight: bold;
          }

          .email {
            font-style: italic;
          }
        }

        .right {
          text-align: right;

          .clearBtn {
            background-color: transparent;
            color: white;
          }
        }
      }

      .el-select {
        .el-input {
          .el-input__inner {
            background-color: transparent;
            color: white;
            border-radius: 0;
            border-top-width: 0;
            border-right-width: 0;
            border-left-width: 0;
          }
        }
      }

      .el-radio-group {
        display: flex;
        flex-direction: column;
        justify-content: space-around;
        height: 45px;

        .el-radio__inner {
          background-color: transparent;

          &:hover {
            border-color: white;
          }
        }

        .el-radio.is-checked {
          .el-radio__label {
            color: white;
          }

          .el-radio__inner {
            border-color: white;
          }
        }
      }
    }
  }

  .leadersList {
    margin-bottom: 20px;

    .el-table__empty-text {
      text-align: center;
      font-style: italic;
      line-height: 150px;
    }
  }
}

.lineManagerConfirmation {
  .el-message-box__btns {
    text-align: center;

    .el-message-box__content {
      padding: 25px 15px;
    }

    button {
      width: 45%;
    }
  }
}
</style>
